import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import { getAriaDescribedByValue, getKey } from "core/model/utils/strings";
import CheckboxInputField, {
  CheckboxProps,
} from "ds_legacy/components/CheckboxInputField";
import { TEXT_DARK_PRIMARY } from "ds_legacy/materials/colors";
import { margin } from "ds_legacy/materials/metrics";
import {
  FONT_FAMILY,
  FONT_SIZE_16,
  LINE_HEIGHT_28,
} from "ds_legacy/materials/typography";
import { CSSProperties, Fragment, ReactNode } from "react";
import {
  FormWatcher,
  StateDispatcher,
  StateDispatcherProps,
  isValid,
} from "react-forms-state";
import { SubheadingFormLegend } from "../FormComponents/SubheadingFormLegend";
import { Validation } from "../Validation";

export type CheckboxGroupBaseProps = {
  LabelInfo?: ReactNode;
  elementName: string;
  exclude?: Array<number>;
  formControlStyle?: CSSProperties;
  formGroupStyle?: CSSProperties;
  include?: Array<number>;
  label?: string;
  labelStyle?: CSSProperties;
  labelWrapperStyle?: CSSProperties;
  sideMutation?: CheckboxProps["sideMutation"];
  wrapperStyle?: CSSProperties;
};

export type CheckboxGroupProps = CheckboxGroupBaseProps & {
  checkboxLabelStyle?: CheckboxProps["labelStyleOverride"];
  describedBy?: string;
  withFlatModel?: boolean;
  items: Array<{
    RadioCaption?: string | null;
    id: number;
    label: string;
  }>;
  label: string; // Note: Moved from optional in base type to required here
  isRequired?: boolean;
};

function convertIn(
  inValue: any = [],
  props: { items: { id: number; label: string; value: any }[] },
) {
  return props.items.map((v: { id: number; label: string; value: any }) => ({
    id: v.id.toString(),
    label: v.label,
    value: (!inValue || inValue.constructor !== Array ? [] : inValue).includes(
      v.id,
    ),
  }));
}

function convertOut(outValue: AnyObject) {
  return outValue
    .filter((v: ToType) => v.value)
    .map((v: ToType) => (isNaN(v.id) ? v.id : parseInt(v.id)));
}

/** @deprecated Use {@link CheckboxGroupV2} from `ds_legacy/components/CheckboxGroupV2` instead. */
function CheckboxGroup({
  checkboxLabelStyle,
  describedBy,
  elementName,
  exclude,
  formControlStyle,
  formGroupStyle,
  include,
  isRequired,
  items,
  label,
  LabelInfo,
  labelStyle,
  labelWrapperStyle,
  sideMutation,
  wrapperStyle,
}: CheckboxGroupProps) {
  const errorTextId = "error-text-id";

  const labelElement = (
    <SubheadingFormLegend
      component="legend"
      required={isRequired}
      sx={{
        color: TEXT_DARK_PRIMARY,
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZE_16,
        lineHeight: LINE_HEIGHT_28,
        margin: margin(0, 0, 1),
        wordBreak: "break-all",
        ...labelStyle,
      }}
    >
      {label}
    </SubheadingFormLegend>
  );

  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValidation }) => {
        const valid = isValid(watchedValidation);
        return (
          <FormControl
            component="fieldset"
            error={!valid}
            style={formControlStyle}
          >
            {/* Wrapping the fieldset in a div for Safari to address flexbox issues, as Safari doesn't treat legend as a flex item correctly.  */}
            {labelWrapperStyle ? (
              <div style={labelWrapperStyle}>
                {labelElement}
                {LabelInfo}
              </div>
            ) : (
              <>
                {labelElement}
                {LabelInfo}
              </>
            )}
            <div
              className="flex flex-col"
              data-testid={`checkbox-group-${elementName}`}
              style={wrapperStyle}
            >
              <FormGroup sx={{ height: "inherit", ...formGroupStyle }}>
                {items.map(({ id, label, RadioCaption }) => (
                  <Fragment key={getKey(id, label)}>
                    {RadioCaption && <RadioCaption />}
                    <CheckboxInputField
                      elementName={id.toString()}
                      hasFieldValidation={valid}
                      inputProps={{
                        "aria-describedby":
                          getAriaDescribedByValue([!valid && errorTextId]) ||
                          describedBy,
                      }}
                      isGroup
                      isHidden={
                        exclude?.includes(id) ||
                        (include && !include.includes(id)) ||
                        false
                      }
                      label={label}
                      labelStyleOverride={checkboxLabelStyle}
                      sideMutation={sideMutation}
                    />
                  </Fragment>
                ))}
              </FormGroup>
              <Validation
                elementName={elementName}
                hasCustomValidation
                id={errorTextId}
                validation={watchedValidation}
              />
            </div>
          </FormControl>
        );
      }}
    </FormWatcher>
  );
}

const ConnectedCheckboxGroup = StateDispatcher(
  convertIn,
  convertOut,
)<CheckboxGroupProps & StateDispatcherProps>(CheckboxGroup);

export default ConnectedCheckboxGroup;
